/**
 * WHAT EACH PERSON FINISHED, AND WHAT IS STILL SITTING ON THEM.
 *
 * Anthony: "admin should be able to see a task completion metric under
 * team."
 *
 * ─── WHAT THIS COUNTS, AND WHY NOT A PERCENTAGE ────────────────────────
 *
 * The obvious metric is jobs finished over jobs assigned, as a percentage.
 * It is also the one that misleads hardest. A technician handed two easy
 * jobs and finishing both scores 100%; one handed nine and finishing seven
 * scores 78% and did three times the work. Ranking a crew by that number
 * quietly tells everybody to take fewer jobs.
 *
 * So the count comes first and the rate second, and the rate is only shown
 * where there is enough behind it to mean anything. Small numbers do not
 * get a percentage at all.
 *
 * ─── WHAT COUNTS AS FINISHED ───────────────────────────────────────────
 *
 * Paid. Not "completed", which means the car is done and the money is not,
 * and not "invoiced", which means the bill went out. Both of those are
 * still open work as far as the shop is concerned, and a metric that calls
 * them done is a metric that rewards leaving money on the table.
 *
 * ─── AND IT STARTS AT THE WATERMARK ────────────────────────────────────
 *
 * 1,774 jobs came from the migration with a timestamp from the night it
 * ran. Counting those against a person would say somebody finished eleven
 * hundred jobs in one evening. The window never reaches back past it.
 */

import { db } from "./client.ts";
import { IMPORT_WATERMARK } from "../domain/watermark.ts";

export type Completion = {
  technicianId: string;
  /** Jobs put on them that reached paid. */
  finished: number;
  /** Still open: quoted, booked, in the bay, done but unbilled, billed. */
  open: number;
  /** Of the open ones, how many are past the day they were booked for. */
  late: number;
  /** finished / (finished + open), or null when there is too little to say. */
  rate: number | null;
};

/** Below this, a percentage is noise dressed as a measurement. */
const ENOUGH = 4;

export async function completionByTech(sinceISO?: string): Promise<Map<string, Completion>> {
  const sb = await db();
  const from = sinceISO && sinceISO > IMPORT_WATERMARK ? sinceISO : IMPORT_WATERMARK;

  type Row = {
    technician_id: string;
    jobs: { status: string; scheduled_at: string | null; created_at: string } | null;
  };

  const { data, error } = await sb.from("job_technicians")
    .select("technician_id,jobs!inner(status,scheduled_at,created_at)")
    .gte("jobs.created_at", from)
    .eq("jobs.is_training", false)
    .limit(2000);
  if (error) throw new Error(error.message);

  const out = new Map<string, Completion>();
  const now = Date.now();

  for (const r of ((data ?? []) as unknown as Row[])) {
    const j = r.jobs;
    if (!j) continue;
    // Cancelled work is neither finished nor outstanding. Counting it as
    // either one punishes somebody for a customer changing their mind.
    if (j.status === "cancelled") continue;

    const c = out.get(r.technician_id)
      ?? { technicianId: r.technician_id, finished: 0, open: 0, late: 0, rate: null };
    if (j.status === "paid") c.finished += 1;
    else {
      c.open += 1;
      if (j.status === "scheduled" && j.scheduled_at
          && Date.parse(j.scheduled_at) < now) c.late += 1;
    }
    out.set(r.technician_id, c);
  }

  for (const c of out.values()) {
    const total = c.finished + c.open;
    c.rate = total >= ENOUGH ? c.finished / total : null;
  }
  return out;
}
